import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;

export const PendingLearnersDialog = ({ programmeId, headers, drill, onClose }) => {
  const [rows, setRows] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!drill) return;
    setRows(null);
    axios.get(`${API}/programmes/${programmeId}/insights/pending`, { headers, params: { kind: drill.kind, id: drill.id } })
      .then((r) => setRows(r.data.learners))
      .catch(() => { toast.error('Could not load learners'); setRows([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill, programmeId]);

  const remind = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/programmes/${programmeId}/insights/remind`, { kind: drill.kind, id: drill.id }, { headers });
      const { sent, skipped_recent: skipped } = r.data;
      if (sent) toast.success(`Reminder sent to ${sent} learner${sent === 1 ? '' : 's'}${skipped ? ` · ${skipped} already reminded in the last 12h` : ''}`);
      else toast.info(skipped ? 'Everyone here was already reminded in the last 12 hours' : 'No learners with an account to remind');
      onClose();
    } catch (e) {
      toast.error('Could not send reminders');
    } finally {
      setSending(false);
    }
  };

  const verb = drill?.kind === 'assignment' ? "haven't submitted" : "haven't finished";
  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-lg" data-testid="insights-pending-dialog">
        <DialogHeader>
          <DialogTitle>{drill?.title}</DialogTitle>
          <DialogDescription className="text-zinc-400">Learners who {verb} yet</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto -mx-1 px-1">
          {rows === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-teal-400" /></div>
            : !rows.length ? <p className="text-sm text-teal-300 py-6 text-center" data-testid="insights-pending-none">Everyone is done. Nice.</p>
              : rows.map((r) => (
                <div key={r.email} className="flex items-center justify-between gap-3 py-2.5 border-b border-zinc-800/70 last:border-0" data-testid="insights-pending-row">
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-100 truncate">{r.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{r.email}{r.user_id ? '' : ' · no account yet'}</p>
                  </div>
                  <span className={`text-xs shrink-0 ${r.detail === 'Overdue' ? 'text-rose-300' : 'text-zinc-400'}`}>{r.detail}</span>
                </div>
              ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300" data-testid="insights-pending-close">Close</Button>
          <Button onClick={remind} disabled={sending || !rows?.some((r) => r.user_id)} className="bg-amber-400 hover:bg-amber-300 text-black" data-testid="insights-send-reminder-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BellRing className="h-4 w-4 mr-2" />}Send reminder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
